import React, { useEffect, useState, useRef, createElement } from 'react';
import { Note, User } from '../../types';
import { Avatar } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { RichTextEditor } from './RichTextEditor';
import { Clock, FileText, Phone, Mail, CheckCircle, AlertCircle, MessageSquare, Lock, User as UserIcon, Reply, Send, ChevronDown, ChevronUp, AtSign, X } from 'lucide-react';
interface NotesBoardViewProps {
  notes: Note[];
  onReply: (parentId: string, content: string, taggedUsers?: User[]) => void;
  availableUsers?: User[];
}
const CATEGORIES = [{
  id: 'General',
  label: 'General',
  color: 'slate',
  icon: MessageSquare
}, {
  id: 'Underwriting',
  label: 'Underwriting',
  color: 'violet',
  icon: AlertCircle
}, {
  id: 'Processing',
  label: 'Processing',
  color: 'blue',
  icon: FileText
}, {
  id: 'Closing',
  label: 'Closing',
  color: 'emerald',
  icon: CheckCircle
}, {
  id: 'Call',
  label: 'Calls',
  color: 'orange',
  icon: Phone
}, {
  id: 'Email',
  label: 'Emails',
  color: 'teal',
  icon: Mail
}] as const;
const CATEGORY_BADGE_VARIANTS: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  Underwriting: 'warning',
  Processing: 'info',
  Closing: 'success',
  Call: 'default',
  Email: 'default',
  General: 'default'
};
const AVATAR_COLORS: Record<string, string> = {
  u1: 'bg-blue-100 text-blue-700',
  u2: 'bg-slate-100 text-slate-600',
  u3: 'bg-purple-100 text-purple-700',
  u4: 'bg-green-100 text-green-700',
  u5: 'bg-orange-100 text-orange-700',
  me: 'bg-teal-100 text-teal-700'
};
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric'
  }).format(date);
}
function formatFullDate(dateString: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(dateString));
}
function getPreviewText(html: string): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}
function getHeaderStyles(color: string): string {
  const styles: Record<string, string> = {
    slate: 'bg-slate-50 border-slate-400 text-slate-700',
    violet: 'bg-violet-50 border-violet-500 text-violet-700',
    blue: 'bg-blue-50 border-blue-500 text-blue-700',
    emerald: 'bg-emerald-50 border-emerald-500 text-emerald-700',
    orange: 'bg-orange-50 border-orange-500 text-orange-700',
    teal: 'bg-teal-50 border-teal-500 text-teal-700'
  };
  return styles[color] || styles.slate;
}
// ─── Read-only note detail shown inside the modal ───────────────────────────
function NoteDetailView({
  note,
  onReply,
  availableUsers = []
}: {
  note: Note;
  onReply: (parentId: string, content: string, taggedUsers?: User[]) => void;
  availableUsers?: User[];
}) {
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [repliesExpanded, setRepliesExpanded] = useState(false);
  const [replyTaggedUsers, setReplyTaggedUsers] = useState<User[]>([]);
  const [replyTagPickerOpen, setReplyTagPickerOpen] = useState(false);
  const [replyTagSearch, setReplyTagSearch] = useState('');
  const replyTagPickerRef = useRef<HTMLDivElement>(null);
  const replyTagSearchRef = useRef<HTMLInputElement>(null);
  const avatarColor = AVATAR_COLORS[note.author.id] || 'bg-slate-100 text-slate-600';
  const categoryVariant = CATEGORY_BADGE_VARIANTS[note.category] || 'default';
  const replyCount = note.replies?.length || 0;
  // Close reply tag picker on click outside
  useEffect(() => {
    if (!replyTagPickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (replyTagPickerRef.current && !replyTagPickerRef.current.contains(e.target as Node)) {
        setReplyTagPickerOpen(false);
        setReplyTagSearch('');
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setReplyTagPickerOpen(false);
        setReplyTagSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [replyTagPickerOpen]);
  useEffect(() => {
    if (replyTagPickerOpen && replyTagSearchRef.current) {
      replyTagSearchRef.current.focus();
    }
  }, [replyTagPickerOpen]);
  const toggleReplyTagUser = (user: User) => {
    setReplyTaggedUsers(prev => prev.some(u => u.id === user.id) ? prev.filter(u => u.id !== user.id) : [...prev, user]);
  };
  const filteredReplyUsers = availableUsers.filter(u => u.name.toLowerCase().includes(replyTagSearch.toLowerCase()) || u.role.toLowerCase().includes(replyTagSearch.toLowerCase()));
  const handleSubmitReply = () => {
    if (!replyContent.trim()) return;
    onReply(note.id, replyContent, replyTaggedUsers.length > 0 ? replyTaggedUsers : undefined);
    setReplyContent('');
    setReplyTaggedUsers([]);
    setIsReplying(false);
  };
  return <div className="space-y-5" data-id="element-111">
      {/* Subject + category */}
      <div className="flex items-start justify-between gap-3" data-id="element-112">
        <h3 className="text-lg font-semibold text-slate-900 leading-snug flex-1" data-id="element-113">
          {note.subject}
        </h3>
        <div className="flex items-center gap-2 shrink-0 mt-0.5" data-id="element-114">
          <Badge variant={categoryVariant} data-id="element-115">{note.category}</Badge>
          {note.isPrivate && <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-id="element-116">
              <Lock className="h-3 w-3" data-id="element-117" />
              Private
            </span>}
        </div>
      </div>

      {/* Author + timestamp */}
      <div className="flex items-center gap-3 py-3 border-y border-slate-100" data-id="element-118">
        <Avatar initials={note.author.initials} size="md" className={avatarColor} data-id="element-119" />
        <div data-id="element-120">
          <p className="text-sm font-semibold text-slate-800" data-id="element-121">
            {note.author.name}
          </p>
          <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5" data-id="element-122">
            <UserIcon className="h-3 w-3" data-id="element-123" />
            {note.author.role}
          </p>
        </div>
        <div className="ml-auto text-right" data-id="element-124">
          <p className="text-xs text-slate-500 flex items-center gap-1 justify-end" data-id="element-125">
            <Clock className="h-3 w-3" data-id="element-126" />
            {formatRelativeTime(note.createdAt)}
          </p>
          <p className="text-xs text-slate-400 mt-0.5" data-id="element-127">
            {formatFullDate(note.createdAt)}
          </p>
        </div>
      </div>

      {/* Rich text body */}
      <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed min-h-[80px]" dangerouslySetInnerHTML={{
      __html: note.content
    }} data-id="element-128" />

      {/* Replies Section */}
      {replyCount > 0 && <div className="mt-6 border-t border-slate-100 pt-4" data-id="element-129">
          <button onClick={() => setRepliesExpanded(!repliesExpanded)} className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors w-full" data-id="element-130">
            {repliesExpanded ? <ChevronUp className="h-4 w-4" data-id="element-131" /> : <ChevronDown className="h-4 w-4" data-id="element-132" />}
            {replyCount} {replyCount === 1 ? 'Reply' : 'Replies'}
          </button>
          {repliesExpanded && <div className="space-y-3 mt-3" data-id="element-133">
              {note.replies!.map(reply => <div key={reply.id} className="bg-slate-50 rounded-lg p-4 border border-slate-100" data-id="element-134">
                  <div className="flex items-center gap-2 mb-2" data-id="element-135">
                    <Avatar initials={reply.author.initials} size="xs" className="h-6 w-6 text-[10px]" data-id="element-136" />
                    <span className="text-sm font-semibold text-slate-800" data-id="element-137">
                      {reply.author.name}
                    </span>
                    <span className="text-xs text-slate-400" data-id="element-138">
                      • {formatRelativeTime(reply.createdAt)}
                    </span>
                  </div>
                  <div className="prose prose-sm max-w-none text-slate-600" dangerouslySetInnerHTML={{
            __html: reply.content
          }} data-id="element-139" />
                  {/* Tagged users on reply */}
                  {reply.taggedUsers && reply.taggedUsers.length > 0 && <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-id="element-140">
                      <AtSign className="h-3 w-3 text-blue-400 shrink-0" data-id="element-141" />
                      {reply.taggedUsers.map(user => <span key={user.id} className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full" data-id="element-142">
                          {user.name}
                        </span>)}
                    </div>}
                </div>)}
            </div>}
        </div>}

      {/* Reply Composer */}
      {isReplying ? <div className="mt-6 border-t border-slate-100 pt-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-143">
          <h4 className="text-sm font-semibold text-slate-900 mb-2" data-id="element-144">
            Write a reply
          </h4>
          {/* Tagged user pills */}
          {replyTaggedUsers.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2" data-id="element-145">
              {replyTaggedUsers.map(user => <span key={user.id} className="inline-flex items-center gap-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full" data-id="element-146">
                  @{user.name}
                  <button type="button" onClick={() => setReplyTaggedUsers(prev => prev.filter(u => u.id !== user.id))} className="text-blue-400 hover:text-blue-600 transition-colors" data-id="element-147">
                    <X className="h-3 w-3" data-id="element-148" />
                  </button>
                </span>)}
            </div>}
          <div className="mb-3" data-id="element-149">
            <RichTextEditor value={replyContent} onChange={setReplyContent} placeholder="Type your reply here..." minHeight="120px" data-id="element-150" />
          </div>
          <div className="flex items-center justify-between gap-2" data-id="element-151">
            {/* Tag button */}
            <div className="relative" ref={replyTagPickerRef} data-id="element-152">
              <button type="button" onClick={() => setReplyTagPickerOpen(!replyTagPickerOpen)} className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md transition-colors ${replyTaggedUsers.length > 0 ? 'text-blue-700 bg-blue-50 border border-blue-200' : 'text-slate-500 hover:bg-slate-100 border border-transparent'}`} data-id="element-153">
                <AtSign className="h-3.5 w-3.5" data-id="element-154" />
                Tag
                {replyTaggedUsers.length > 0 ? ` (${replyTaggedUsers.length})` : ''}
              </button>
              {replyTagPickerOpen && <div className="absolute left-0 bottom-full mb-1 w-60 bg-white rounded-lg border border-slate-200 shadow-lg z-30 animate-in fade-in slide-in-from-bottom-2 duration-150" data-id="element-155">
                  <div className="p-2 border-b border-slate-100" data-id="element-156">
                    <input ref={replyTagSearchRef} type="text" placeholder="Search users..." value={replyTagSearch} onChange={e => setReplyTagSearch(e.target.value)} className="w-full text-xs px-2.5 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-slate-400" data-id="element-157" />
                  </div>
                  <div className="max-h-44 overflow-y-auto py-1" data-id="element-158">
                    {filteredReplyUsers.length === 0 ? <p className="text-xs text-slate-400 text-center py-3" data-id="element-159">
                        No users found
                      </p> : filteredReplyUsers.map(user => {
                const isSelected = replyTaggedUsers.some(u => u.id === user.id);
                return <button key={user.id} type="button" onClick={() => toggleReplyTagUser(user)} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`} data-id="element-160">
                            <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${isSelected ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-161">
                              {user.initials}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-162">
                              <p className="text-xs font-medium text-slate-800 truncate" data-id="element-163">
                                {user.name}
                              </p>
                              <p className="text-[10px] text-slate-400 truncate" data-id="element-164">
                                {user.role}
                              </p>
                            </div>
                            {isSelected && <div className="h-4 w-4 rounded-full bg-blue-600 flex items-center justify-center shrink-0" data-id="element-165">
                                <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-166">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-167" />
                                </svg>
                              </div>}
                          </button>;
              })}
                  </div>
                </div>}
            </div>
            <div className="flex items-center gap-2" data-id="element-168">
              <Button variant="ghost" size="sm" onClick={() => {
            setIsReplying(false);
            setReplyTaggedUsers([]);
          }} data-id="element-169">
                Cancel
              </Button>
              <Button size="sm" onClick={handleSubmitReply} disabled={!replyContent.trim()} data-id="element-170">
                <Send className="h-3 w-3 mr-1.5" data-id="element-171" />
                Post Reply
              </Button>
            </div>
          </div>
        </div> : <div className="mt-6 pt-4 border-t border-slate-100" data-id="element-172">
          <Button variant="secondary" className="w-full" onClick={() => setIsReplying(true)} data-id="element-173">
            <Reply className="h-4 w-4 mr-2" data-id="element-174" />
            Reply to Note
          </Button>
        </div>}
    </div>;
}
// ─── Main board view ─────────────────────────────────────────────────────────
export function NotesBoardView({
  notes,
  onReply,
  availableUsers = []
}: NotesBoardViewProps) {
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const selectedNote = selectedNoteId ? notes.find(n => n.id === selectedNoteId) || null : null;
  return <>
      <div className="w-full h-[calc(100vh-220px)] overflow-x-auto pb-4" data-id="element-175">
        <div className="flex gap-4 min-w-max px-2 h-full" data-id="element-176">
          {CATEGORIES.map(category => {
          const categoryNotes = notes.filter(n => n.category === category.id);
          const headerStyle = getHeaderStyles(category.color);
          const Icon = category.icon;
          return <div key={category.id} className="w-72 flex flex-col h-full flex-shrink-0" data-id="element-177">
                {/* Column header */}
                <div className={`flex items-center justify-between px-4 py-3 border-t-4 rounded-t-lg ${headerStyle} shadow-sm z-10 sticky top-0`} data-id="element-178">
                  <div className="flex items-center gap-2" data-id="element-179">
                    <Icon className="h-4 w-4 opacity-75" data-id="element-180" />
                    <span className="font-semibold text-sm" data-id="element-181">
                      {category.label}
                    </span>
                  </div>
                  <span className="text-xs font-medium bg-white/60 px-2 py-0.5 rounded-full min-w-[1.5rem] text-center" data-id="element-182">
                    {categoryNotes.length}
                  </span>
                </div>

                {/* Column body */}
                <div className="flex-1 overflow-y-auto p-2 bg-slate-50/50 border-x border-b border-slate-200 rounded-b-lg space-y-3" data-id="element-183">
                  {categoryNotes.length === 0 ? <div className="text-center py-8 text-slate-400 text-xs italic" data-id="element-184">
                      No notes
                    </div> : categoryNotes.map(note => <button key={note.id} onClick={() => setSelectedNoteId(note.id)} className="w-full text-left bg-white p-3 rounded-md border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-185">
                        {/* Card header */}
                        <div className="flex justify-between items-start mb-2" data-id="element-186">
                          <h4 className="text-sm font-semibold text-slate-800 line-clamp-2 leading-tight group-hover:text-blue-700 transition-colors" data-id="element-187">
                            {note.subject}
                          </h4>
                          {note.isPrivate && <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded ml-2 shrink-0 border border-amber-100" data-id="element-188">
                              PRIVATE
                            </span>}
                        </div>

                        {/* Preview text */}
                        <p className="text-xs text-slate-500 line-clamp-3 mb-3 leading-relaxed" data-id="element-189">
                          {getPreviewText(note.content)}
                        </p>

                        {/* Card footer */}
                        <div className="flex items-center justify-between pt-2 border-t border-slate-100" data-id="element-190">
                          <div className="flex items-center gap-1.5" data-id="element-191">
                            <Avatar initials={note.author.initials} size="sm" className={`h-5 w-5 text-[10px] ${AVATAR_COLORS[note.author.id] || 'bg-slate-100 text-slate-600'}`} data-id="element-192" />
                            <span className="text-xs text-slate-600 truncate max-w-[80px]" data-id="element-193">
                              {note.author.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-194">
                            {note.replies && note.replies.length > 0 && <span className="text-[10px] text-slate-400 flex items-center gap-0.5 bg-slate-50 px-1.5 py-0.5 rounded" data-id="element-195">
                                <MessageSquare className="h-3 w-3" data-id="element-196" />
                                {note.replies.length}
                              </span>}
                            <span className="text-[10px] text-slate-400 flex items-center gap-1 bg-slate-50 px-1.5 py-0.5 rounded" data-id="element-197">
                              <Clock className="h-3 w-3" data-id="element-198" />
                              {formatRelativeTime(note.createdAt)}
                            </span>
                          </div>
                        </div>
                      </button>)}
                </div>
              </div>;
        })}
        </div>
      </div>

      {/* Note detail modal */}
      <Modal isOpen={selectedNote !== null} onClose={() => setSelectedNoteId(null)} title="Note Detail" data-id="element-199">
        {selectedNote && <NoteDetailView note={selectedNote} onReply={onReply} availableUsers={availableUsers} data-id="element-200" />}
      </Modal>
    </>;
}